import React, { FC, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { shallow } from 'zustand/shallow';
import { useRouter } from 'next/router';
import { useB2CCorporateUser, useTranslation } from '$hooks';
import { useAuthentication, useBasket, useDisabledFeatures } from '~/store';
import { B2CDeliveryMethod, BuyButtonState } from '~/constants';
import { IEnrichedProduct } from '$models';
import { FlexContent, Flex } from '$components/layouts';
import { Button } from '$components/elements/button';
import { SvgIcon } from '$components/elements/svg-icon';
import { mq } from '$lib/helpers';
import { PickupPanel, StockSummary } from './components';
import { useProductStock } from './hooks';

type Props = {
    selectedVariant?: IEnrichedProduct;
    selectedZipCode?: string;
    findStoreUrl?: string;
};

export const ProductPurchase: FC<Props> = ({ selectedVariant, selectedZipCode, findStoreUrl }: Props): JSX.Element => {
    const { addToBasket } = useBasket();
    const { push } = useRouter();
    const [quantity] = useState(1);
    const { translate } = useTranslation();
    const theme = useTheme();
    const [isPickupPanelOpen, setIsPickupPanelOpen] = useState(false);
    const { initialAppUserFetchCompleted, loadingAppUser } = useAuthentication(
        (state) => ({
            initialAppUserFetchCompleted: state.initialAppUserFetchCompleted,
            loadingAppUser: state.loadingAppUser,
        }),
        shallow
    );
    const { corporateInfo, isLoading } = useB2CCorporateUser();
    const { stockCount, onStoreSelection, selectedStore, selectedStoreStockType } = useProductStock({
        productStock: selectedVariant?.stockStatusForShops,
    });
    const { isQuickCheckoutDisabled } = useDisabledFeatures();

    const shouldDisplayStock = useMemo(() => {
        return (
            // deliveryMethod !== B2CDeliveryMethod.DirectOnly && // IND-11045
            selectedVariant?.b2CDeliveryMethod !== B2CDeliveryMethod.Download &&
            selectedVariant?.b2CDeliveryMethod !== B2CDeliveryMethod.Preorder &&
            selectedVariant?.b2CDeliveryMethod !== B2CDeliveryMethod.PreorderShopOnly &&
            selectedVariant?.b2CDeliveryMethod !== B2CDeliveryMethod.PreorderDirectOnly &&
            selectedVariant?.b2CDeliveryMethod !== B2CDeliveryMethod.PreorderShopAndDirect &&
            selectedVariant?.b2CDeliveryMethod !== B2CDeliveryMethod.AvailableSoon
        );
    }, [selectedVariant?.b2CDeliveryMethod]);

    const shouldDisplayClickCollect = useMemo(() => {
        if (!isLoading && corporateInfo?.b2CCorporateUser) {
            return false;
        }

        if (isQuickCheckoutDisabled) {
            return false;
        }

        return (
            selectedVariant?.buttonState === BuyButtonState.Enabled &&
            (selectedVariant.b2CDeliveryMethod === B2CDeliveryMethod.ShopAndDirect ||
                selectedVariant.b2CDeliveryMethod === B2CDeliveryMethod.ShopOnly) &&
            Object.values(selectedVariant?.stockStatusForShops ?? {}).some((stock) => stock > 0)
        );
    }, [
        selectedVariant?.buttonState,
        selectedVariant?.b2CDeliveryMethod,
        selectedVariant?.stockStatusForShops,
        corporateInfo,
        isLoading,
        isQuickCheckoutDisabled,
    ]);

    // TODO: Talk to BE and see if they can return the correct B2CDeliveryMethod for
    // the time where selectedVariant?.scomStockOverrideHqEnabled && selectedVariant?.scomStockOverrideHqCount === 0
    const getButtonText = () => {
        if (selectedVariant?.scomStockOverrideHqEnabled && selectedVariant?.scomStockOverrideHqCount === 0) {
            return translate('productPage.productDetails.soldOut');
        }

        switch (selectedVariant?.b2CDeliveryMethod) {
            case B2CDeliveryMethod.DirectOnly:
            case B2CDeliveryMethod.ShopAndDirect:
            case B2CDeliveryMethod.Download:
                return translate('productPage.productDetails.addToBasket');
            case B2CDeliveryMethod.NotPossible:
                return translate('productPage.productDetails.notAvailableOnWeb');
            case B2CDeliveryMethod.ShopOnly:
                return translate('productPage.productDetails.clickAndCollect');
            case B2CDeliveryMethod.PreorderDirectOnly:
                return translate('productPage.productDetails.preorderDirectOnlyLabel');
            case B2CDeliveryMethod.PreorderShopAndDirect:
                return translate('productPage.productDetails.preorderShopAndDirectLabel');
            case B2CDeliveryMethod.PreorderShopOnly:
                return translate('productPage.productDetails.preorderShopOnlyLabel');
            case B2CDeliveryMethod.PrintOnDemand:
                return translate('productPage.productDetails.printOnDemand');
            case B2CDeliveryMethod.Preorder:
                return translate('productPage.productDetails.addToBasketPreOrder');
            case B2CDeliveryMethod.AvailableSoon:
                return translate('productPage.productDetails.availableSoon');
            default:
                return translate('productPage.productDetails.addToBasket');
        }
    };

    return (
        <>
            <Flex column gap="extrasmall" spacingBottom="small">
                <FlexContent grow={1}>
                    {selectedVariant?.buttonState === BuyButtonState.Enabled && (
                        <BasketButton
                            width="expansive"
                            onClick={() => addToBasket(selectedVariant?.product?.itemNumber || '', quantity)}
                            loading={!initialAppUserFetchCompleted && loadingAppUser}
                        >
                            <PurchaseIcon color="white" size={24} svg="basket" />
                            {getButtonText()}
                        </BasketButton>
                    )}
                    {selectedVariant?.buttonState === BuyButtonState.Disabled &&
                        selectedVariant.b2CDeliveryMethod !== B2CDeliveryMethod.NotPossible && (
                            <BasketButton
                                width="expansive"
                                disabled
                                onClick={() => addToBasket(selectedVariant?.product?.itemNumber || '', quantity)}
                            >
                                {getButtonText()}
                            </BasketButton>
                        )}
                    {selectedVariant?.buttonState === BuyButtonState.Disabled &&
                        selectedVariant.b2CDeliveryMethod === B2CDeliveryMethod.NotPossible && (
                            <BasketButton
                                width="expansive"
                                onClick={findStoreUrl ? () => push(findStoreUrl) : undefined}
                            >
                                <StyledButtonIcon color={theme.colors.red} size={30} svg="pin1" />
                                {translate('productPage.productDetails.isOutOfSaleOnlineButtonLabel')}
                            </BasketButton>
                        )}
                </FlexContent>
                {shouldDisplayClickCollect && (
                    <BasketButton onClick={() => setIsPickupPanelOpen(true)} variant="inverted" width="expansive">
                        <PurchaseIcon color="transparent" size={24} svg="clockfast2" />
                        {translate('productPage.clickCollect.buttonText')}
                    </BasketButton>
                )}
            </Flex>
            {shouldDisplayStock && (
                <StockSummary
                    deliveryMethod={selectedVariant?.b2CDeliveryMethod}
                    setPanelVisible={setIsPickupPanelOpen}
                    selectedStore={selectedStore}
                    selectedStoreStockType={selectedStoreStockType}
                    stockCount={stockCount}
                />
            )}
            {isPickupPanelOpen && (
                <PickupPanel
                    canPickup={shouldDisplayClickCollect}
                    isOpen={isPickupPanelOpen}
                    setIsOpen={setIsPickupPanelOpen}
                    productStock={selectedVariant?.stockStatusForShops}
                    itemNumber={selectedVariant?.product?.itemNumber}
                    stockCount={stockCount}
                    onStoreSelection={onStoreSelection}
                    selectedStore={selectedStore}
                    selectedZipCode={selectedZipCode}
                    purchaseAvailable={
                        selectedVariant?.buttonState === BuyButtonState.Enabled && isQuickCheckoutDisabled
                    }
                    purchaseText={getButtonText()}
                />
            )}
        </>
    );
};

const StyledButtonIcon = styled(SvgIcon)(({ theme }) => ({
    padding: '5px',
    marginRight: theme.space[3],
    backgroundColor: theme.colors.white,
    borderRadius: '50%',
}));

const PurchaseIcon = styled(SvgIcon)(({ theme }) => ({
    marginRight: theme.space[3],
}));

const BasketButton = styled(Button)({
    [mq('md')]: {
        // maxWidth: 270,
    },
});
